import type { ThemeConfig } from 'antd';

import type { NextTokens } from '@ui-library/tokens/types';

/**
 * Shared global token overrides for "next" variant components.
 * These apply to any Ant component inside the ConfigProvider.
 */
// ts-prune-ignore-next
export function buildNextGlobalTokens(next: NextTokens): ThemeConfig['token'] {
  return {
    controlHeight: parseInt(next.controlHeight, 10),
    controlHeightLG: parseInt(next.controlHeightLG, 10),
    controlHeightSM: parseInt(next.controlHeightSM, 10),
    borderRadius: parseInt(next.borderRadius, 10),
    borderRadiusLG: parseInt(next.borderRadius, 10),
    borderRadiusSM: parseInt(next.borderRadius, 10),
    colorBorder: next.colorBorder,
    colorError: next.colorError,
    colorText: next.colorText,
    colorTextPlaceholder: next.colorTextPlaceholder,
    colorTextDisabled: next.palette.grey400,
    colorBgContainer: next.colorBgField,
    colorBgContainerDisabled: next.colorBgDisabled,
  };
}

/**
 * ConfigProvider theme for:
 * - Input
 * - TextArea
 * - Password
 * - Search
 * - InputNumber
 *
 * with variant="next".
 */
export function buildNextInputTheme(next: NextTokens): ThemeConfig {
  const nextInputComponentTokens = {
    hoverBorderColor: next.colorBorderHover,
    activeBorderColor: next.colorBorderFocus,
    activeShadow: next.boxShadowFocus,
    errorActiveShadow: next.boxShadowError,
  };

  return {
    token: buildNextGlobalTokens(next),
    components: {
      Input: {
        ...nextInputComponentTokens,
      },
      InputNumber: {
        ...nextInputComponentTokens,
      },
    },
  };
}

/**
 * ConfigProvider theme for Select with variant="next".
 */
export function buildNextSelectTheme(next: NextTokens): ThemeConfig {
  return {
    token: buildNextGlobalTokens(next),
    components: {
      Select: {
        selectorBg: next.colorBgField,
        optionSelectedBg: next.palette.grey50,
        optionActiveBg: next.palette.grey50,
        optionSelectedFontWeight: next.fontWeight400,
        optionFontSize: parseInt(next.fontSize200, 10),
        optionHeight: 32,
        optionPadding: '4px 8px',
        multipleItemBg: next.colorBgField,
        multipleItemBorderColor: next.palette.grey75,
        multipleItemHeight: 20,
      },
    },
  };
}

/**
 * ConfigProvider theme for Menu with variant="next".
 */
export function buildNextMenuTheme(next: NextTokens): ThemeConfig {
  return {
    token: buildNextGlobalTokens(next),
    components: {
      Menu: {
        itemBg: next.colorBgField,
        itemSelectedBg: next.palette.grey50,
        itemActiveBg: next.palette.grey50,
        itemSelectedColor: next.colorText,
        itemColor: next.palette.grey800,
        groupTitleColor: next.colorText,
        itemHoverBg: next.palette.grey50,
        itemHoverColor: next.colorText,
        itemHeight: 32,
        fontSize: parseInt(next.fontSize200, 10),
      },
    },
  };
}

/**
 * ConfigProvider theme for Dropdown with variant="next".
 */
export function buildNextDropdownTheme(next: NextTokens): ThemeConfig {
  return {
    token: buildNextGlobalTokens(next),
  };
}

/**
 * ConfigProvider theme for:
 * - DatePicker
 * - RangePicker
 *
 * with variant="next".
 *
 * DatePicker has no input-trigger component tokens (hoverBorderColor, activeShadow, etc.)
 * so focus/hover/error shadows are handled via global tokens + minimal CSS.
 */
// eslint-disable-next-line sonarjs/no-identical-functions -- intentionally separate for clarity
export function buildNextDatePickerTheme(next: NextTokens): ThemeConfig {
  return {
    token: buildNextGlobalTokens(next),
  };
}

/**
 * ConfigProvider theme for Table with variant="next".
 *
 * Minimal header, no row dividers, subtle hover.
 */
export function buildNextTableTheme(next: NextTokens): ThemeConfig {
  return {
    token: {
      ...buildNextGlobalTokens(next),
      colorPrimary: next.colorBorderFocus, // #105ED5
    },
    components: {
      Table: {
        headerBg: 'transparent',
        headerColor: next.palette.grey600,
        headerSplitColor: 'transparent',
        headerBorderRadius: 0,
        borderColor: next.colorBorder,
        rowHoverBg: next.palette.grey50,
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
        fontSize: parseInt(next.fontSize200, 10),
      },
      Button: {
        borderRadius: parseInt(next.borderRadius, 10),
        borderRadiusLG: parseInt(next.borderRadius, 10),
        borderRadiusSM: parseInt(next.borderRadius, 10),
        controlHeight: parseInt(next.controlHeight, 10),
        controlHeightSM: parseInt(next.controlHeightSM, 10),
        contentFontSize: parseInt(next.fontSize200, 10),
        contentFontSizeSM: parseInt(next.fontSize100, 10),
        paddingInline: 8,
      },
    },
  };
}

/**
 * ConfigProvider theme for Tabs with variant="next".
 *
 * Border color (#D8DEE4 / grey200) is handled via styled-components
 * since Ant's Tabs tokens don't expose the nav ::before border.
 */
export function buildNextTabsTheme(next: NextTokens): ThemeConfig {
  return {
    token: buildNextGlobalTokens(next),
    components: {
      Tabs: {
        inkBarColor: next.colorBorderFocus,
        itemColor: next.palette.grey400,
        itemSelectedColor: next.colorBorderFocus,
        itemHoverColor: next.colorText,
      },
    },
  };
}

/**
 * ConfigProvider theme for Checkbox with variant="next".
 *
 * Checkbox has no component tokens in Ant v5.9, so visual overrides
 * are handled via CSS in NextCheckboxWrapper. This theme only applies
 * shared global tokens (border-radius, colors).
 */
export function buildNextCheckboxTheme(next: NextTokens): ThemeConfig {
  return {
    token: {
      ...buildNextGlobalTokens(next),
      colorPrimary: next.palette.grey900,
      colorPrimaryHover: next.palette.grey900,
    },
  };
}

/**
 * ConfigProvider theme for Button with variant="next".
 *
 * Sets shared sizing/radius. Per-level/intent colors are handled via
 * styled-components CSS since Ant's Button tokens don't support multiple
 * visual variants within a single theme.
 */
export function buildNextButtonTheme(next: NextTokens): ThemeConfig {
  return {
    token: {
      ...buildNextGlobalTokens(next),
      colorPrimary: next.colorBorderFocus, // #105ED5
    },
    components: {
      Button: {
        borderRadius: parseInt(next.borderRadius, 10),
        borderRadiusLG: parseInt(next.borderRadius, 10),
        borderRadiusSM: parseInt(next.borderRadius, 10),
        controlHeight: parseInt(next.controlHeight, 10),
        controlHeightLG: parseInt(next.controlHeightLG, 10),
        controlHeightSM: parseInt(next.controlHeightSM, 10),
        contentFontSize: parseInt(next.fontSize200, 10),
        contentFontSizeSM: parseInt(next.fontSize100, 10),
        contentFontSizeLG: parseInt(next.fontSize200, 10),
        paddingInline: 8,
      },
    },
  };
}

/**
 * ConfigProvider theme for Modal with variant="next".
 *
 * Overrides border radius for the modal content.
 */
export function buildNextModalTheme(next: NextTokens): ThemeConfig {
  return {
    token: buildNextGlobalTokens(next),
    components: {
      Modal: {
        borderRadiusLG: 16,
        titleFontSize: 16,
      },
    },
  };
}
